import { Button } from '../../../components/base/Button';
import { useEffect, useState } from 'react';

export const HeroSection = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const parallaxY = scrollY * 0.35;

  return (
    <section
      id="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 w-full h-full"
        style={{ transform: `translateY(${parallaxY}px)` }}
      >
        <img
          src="https://public.readdy.ai/ai/img_res/a4e170a8025e2da7e985da84dcda74c5.jpg"
          alt="BrandBoost hero"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/70 to-white/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-white/60" />
      </div>

      {/* Decorative blobs */}
      <div className="absolute top-20 left-10 w-48 sm:w-72 h-48 sm:h-72 bg-purple-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 right-10 w-64 sm:w-96 h-64 sm:h-96 bg-pink-200/30 rounded-full blur-3xl pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-28 pb-20 sm:py-32 flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
        
        {/* Left: Main content */}
        <div className="w-full max-w-2xl flex-shrink-0 text-center lg:text-left">
          {/* Title */}
          <h1
            className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-black text-gray-900 leading-tight mb-5 sm:mb-6 transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <span className="block">Boostez</span>
            <span className="block bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent">
              votre business
            </span>
            <span className="block text-gray-800">en ligne</span>
          </h1>

          {/* Subtitle */}
          <p
            className={`text-base sm:text-lg md:text-xl text-gray-600 font-accent leading-relaxed mb-8 sm:mb-10 max-w-xl mx-auto lg:mx-0 transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            BrandBoost transforme votre présence digitale en moteur de croissance : plus de visibilité, plus de clients, plus de ventes.
          </p>

          {/* Pills */}
          <div
            className={`flex flex-wrap justify-center lg:justify-start gap-2 sm:gap-3 mb-8 sm:mb-10 transition-all duration-700 delay-300 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            {[
              { icon: 'ri-eye-line', label: 'Plus de visibilité', color: 'from-purple-500 to-purple-600' },
              { icon: 'ri-user-add-line', label: 'Plus de clients', color: 'from-pink-500 to-pink-600' },
              { icon: 'ri-line-chart-line', label: 'Plus de ventes', color: 'from-purple-500 to-pink-500' },
            ].map((pill, i) => (
              <div
                key={i}
                className="flex items-center gap-2 bg-white border border-gray-200 shadow-sm rounded-full px-3 sm:px-4 py-2"
              >
                <div className={`w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full bg-gradient-to-br ${pill.color}`}>
                  <i className={`${pill.icon} text-white text-xs`} />
                </div>
                <span className="text-gray-700 font-accent font-semibold text-xs sm:text-sm">{pill.label}</span>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div
            className={`flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start transition-all duration-700 delay-400 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <Button
              onClick={scrollToContact}
              className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white font-heading font-bold text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer w-full sm:w-auto"
            >
              <span className="flex items-center justify-center gap-2">
                <i className="ri-calendar-check-line text-base sm:text-lg" />
                Rendez-vous gratuit
              </span>
            </Button>
            <button
              onClick={() => {
                const section = document.getElementById('solution-section');
                if (section) section.scrollIntoView({ behavior: 'smooth' });
              }}
              className="flex items-center justify-center gap-2 bg-white border-2 border-gray-200 hover:border-purple-300 text-gray-700 hover:text-purple-600 font-heading font-bold text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4 rounded-full shadow-sm hover:shadow-md transition-all duration-300 whitespace-nowrap cursor-pointer w-full sm:w-auto"
            >
              <i className="ri-play-circle-line text-base sm:text-lg" />
              Découvrir nos services
            </button>
          </div>
        </div>

        {/* Right: Large agency label — desktop only */}
        <div
          className={`hidden lg:flex flex-col items-end justify-center flex-shrink-0 transition-all duration-700 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
          }`}
        >
          <div className="relative flex flex-col items-end gap-2">
            <div className="w-16 h-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mb-2 self-end" />
            <div className="flex flex-col items-end gap-1">
              <span className="text-5xl xl:text-6xl font-heading font-black text-gray-900 leading-none tracking-tight text-right">
                Agence de
              </span>
              <span className="text-5xl xl:text-6xl font-heading font-black bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent leading-none tracking-tight text-right">
                Marketing
              </span>
              <span className="text-5xl xl:text-6xl font-heading font-black text-gray-900 leading-none tracking-tight text-right">
                &amp; Visibilité
              </span>
              <span className="text-5xl xl:text-6xl font-heading font-black bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent leading-none tracking-tight text-right">
                Digitale
              </span>
            </div>
            <div className="w-24 h-1 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full mt-2 self-end" />
            <p className="text-gray-900 text-sm font-accent tracking-widest uppercase mt-3 text-right">
              Stratégie · Croissance · Impact
            </p>
          </div>
        </div>

        {/* Mobile agency label — same format as desktop but smaller */}
        <div
          className={`lg:hidden w-full flex justify-center transition-all duration-700 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="relative flex flex-col items-center gap-2">
            <div className="w-12 h-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mb-1 self-end" />
            <div className="flex flex-col items-end gap-1">
              <span className="text-2xl sm:text-3xl font-heading font-black text-gray-900 leading-none tracking-tight text-right">
                Agence de
              </span>
              <span className="text-2xl sm:text-3xl font-heading font-black bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-transparent leading-none tracking-tight text-right">
                Marketing
              </span>
              <span className="text-2xl sm:text-3xl font-heading font-black text-gray-900 leading-none tracking-tight text-right">
                &amp; Visibilité
              </span>
              <span className="text-2xl sm:text-3xl font-heading font-black bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent leading-none tracking-tight text-right">
                Digitale
              </span>
            </div>
            <div className="w-16 h-1 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full mt-1 self-end" />
            <p className="text-gray-900 text-xs font-accent tracking-widest uppercase mt-2 text-right">
              Stratégie · Croissance · Impact
            </p>
          </div>
        </div>

      </div>

    </section>
  );
};
